import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit3, X, Save, Shield, KeyRound } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null); // { id?, username, password, role, full_name, email }
  const [pwdModal, setPwdModal] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });

  const reload = () => api.adminListUsers().then(setUsers).catch(() => setUsers([]));
  useEffect(() => { reload(); api.adminMe().then(setMe).catch(() => {}); }, []);

  const startNew = () => setEditing({ username: '', password: '', role: 'editor', full_name: '', email: '' });
  const startEdit = (u) => setEditing({ id: u.id, username: u.username, password: '', role: u.role || 'editor', full_name: u.full_name || '', email: u.email || '' });

  const save = async () => {
    try {
      if (editing.id) {
        const payload = { role: editing.role, full_name: editing.full_name, email: editing.email };
        if (editing.password) payload.password = editing.password;
        await api.adminUpdateUser(editing.id, payload);
        toast.success('Kullanıcı güncellendi');
      } else {
        if (!editing.username || !editing.password) {
          toast.error('Kullanıcı adı ve şifre zorunlu'); return;
        }
        await api.adminCreateUser(editing);
        toast.success('Kullanıcı eklendi');
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    }
  };

  const del = async (u) => {
    if (!window.confirm(`"${u.username}" kullanıcısını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.adminDeleteUser(u.id);
      toast.success('Silindi');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  const changePassword = async () => {
    if (pwd.next !== pwd.confirm) { toast.error('Yeni şifreler eşleşmiyor'); return; }
    if (pwd.next.length < 6) { toast.error('Şifre en az 6 karakter olmalı'); return; }
    try {
      await api.adminChangePassword(pwd.current, pwd.next);
      toast.success('Şifre değiştirildi');
      setPwdModal(false);
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Şifre değiştirilemedi');
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Erişim</div>
          <h1 className="font-display text-4xl mt-2 leading-none">Yönetici Kullanıcılar</h1>
          <p className="mt-2 text-ink-400 text-sm">{users.length} aktif kullanıcı</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPwdModal(true)} data-testid="change-my-password" className="bg-cream-200 text-ink-600 rounded-full px-5 h-11 text-sm font-semibold hover:bg-cream-300 inline-flex items-center gap-2 border border-ink-100">
            <KeyRound className="w-4 h-4" /> Şifremi Değiştir
          </button>
          <button onClick={startNew} data-testid="new-user-btn" className="bg-ink-600 text-cream rounded-full px-5 h-11 text-sm font-semibold hover:bg-ink-500 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Yeni Kullanıcı
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-200 text-ink-500 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-5 py-3">Kullanıcı</th>
                <th className="text-left px-5 py-3">Rol</th>
                <th className="text-left px-5 py-3">Ad Soyad</th>
                <th className="text-left px-5 py-3">E-posta</th>
                <th className="text-right px-5 py-3">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-ink-50 hover:bg-cream-100/40" data-testid={`user-row-${u.username}`}>
                  <td className="px-5 py-4 font-semibold text-ink-600">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gold/20 text-ink-600 grid place-items-center font-display font-bold text-sm">
                        {u.username[0].toUpperCase()}
                      </div>
                      <div>
                        <div>{u.username}</div>
                        {me?.username === u.username && <div className="text-[10px] text-coral-300 font-bold tracking-wider">SİZ</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${u.role === 'admin' ? 'bg-ink-600 text-gold' : 'bg-emerald-50 text-emerald-700'}`}>
                      <Shield className="w-3 h-3" /> {u.role || 'admin'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-ink-500">{u.full_name || '—'}</td>
                  <td className="px-5 py-4 text-ink-500">{u.email || '—'}</td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => startEdit(u)} className="text-ink-500 hover:text-ink-600 mr-2" title="Düzenle" data-testid={`edit-user-${u.username}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    {me?.username !== u.username && (
                      <button onClick={() => del(u)} className="text-coral-300 hover:text-coral-400" title="Sil" data-testid={`delete-user-${u.username}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-12 text-center text-ink-400">Kullanıcı yok.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-ink-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl border border-ink-100 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
              <h3 className="font-display text-2xl">{editing.id ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</h3>
              <button onClick={() => setEditing(null)} className="text-ink-400 hover:text-ink-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid gap-4">
              {!editing.id && (
                <Field label="Kullanıcı Adı *" testid="user-username">
                  <input value={editing.username} onChange={e => setEditing({ ...editing, username: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
                </Field>
              )}
              <Field label={editing.id ? 'Yeni Şifre (boş bırakılırsa değişmez)' : 'Şifre *'} testid="user-password">
                <input type="password" value={editing.password} onChange={e => setEditing({ ...editing, password: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
              </Field>
              <Field label="Rol" testid="user-role">
                <select value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm">
                  <option value="admin">Admin (tam yetki)</option>
                  <option value="editor">Editör (sadece içerik)</option>
                </select>
              </Field>
              <Field label="Ad Soyad" testid="user-fullname">
                <input value={editing.full_name} onChange={e => setEditing({ ...editing, full_name: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
              </Field>
              <Field label="E-posta" testid="user-email">
                <input type="email" value={editing.email} onChange={e => setEditing({ ...editing, email: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-ink-100 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-full px-5 h-10 text-sm font-semibold hover:bg-cream-200">İptal</button>
              <button onClick={save} data-testid="user-save" className="bg-gold text-ink-700 rounded-full px-5 h-10 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change own password modal */}
      {pwdModal && (
        <div className="fixed inset-0 z-50 bg-ink-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setPwdModal(false)}>
          <div className="bg-white rounded-2xl border border-ink-100 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
              <h3 className="font-display text-2xl">Şifre Değiştir</h3>
              <button onClick={() => setPwdModal(false)} className="text-ink-400 hover:text-ink-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 grid gap-4">
              <Field label="Mevcut Şifre" testid="current-password">
                <input type="password" value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
              </Field>
              <Field label="Yeni Şifre (en az 6 karakter)" testid="new-password">
                <input type="password" value={pwd.next} onChange={e => setPwd({ ...pwd, next: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
              </Field>
              <Field label="Yeni Şifre (tekrar)" testid="confirm-password">
                <input type="password" value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} className="w-full h-11 px-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 outline-none text-sm" />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-ink-100 flex justify-end gap-2">
              <button onClick={() => setPwdModal(false)} className="rounded-full px-5 h-10 text-sm font-semibold hover:bg-cream-200">İptal</button>
              <button onClick={changePassword} data-testid="change-password-save" className="bg-gold text-ink-700 rounded-full px-5 h-10 text-sm font-semibold hover:bg-gold-400 inline-flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> Değiştir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] uppercase tracking-widest font-bold text-ink-400">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
